import React, {
  useCallback, useEffect, useMemo, useState, Fragment, ReactElement,
} from 'react';
import Table from '../../../components/table/table.component';
import TableHead from '../../../components/table-head/table-head.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import Widget from '../../../components/widget/widget.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import ActionButton from '../../../components/action-button/action-button.component';
import ChartStackSingle from '../../../components/chart-stack-single/chart-stack-single.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import ActionButtonNewItem from '../../../components/action-button-new-item/action-button-new-item.component';
import TableStackChartSingleBlock
  from '../../../components/table-stack-chart-single-block/table-stack-chart-single-block.component';
import TableCellStackChartSingle
  from '../../../components/table-cell-stack-chart-single/table-cell-stack-chart-single.component';
import RemoveIcon from '../../../icons/remove-icon';
import StructureService from '../../../services/structure-service';
import CurrencyService from '../../../services/currency-service';
import FormatService from '../../../services/format-service';
import FormService from '../../../services/form-service';
import {
  DEAL_METRICS_CAPITAL_STACK_ITEM_EDIT, DEAL_METRICS_STATISTICS_ITEM_CREATE, DEAL_METRICS_STATISTICS_ITEM_DELETE,
  DEAL_METRICS_STATISTICS_ITEM_EDIT,
} from '../../../constants/entity/permissions';
import CURRENCY_USD from '../../../constants/system/currencies';

const DealMetrics: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const WIDGET_TITLE_DEFAULT: string = 'Deal Metrics';
  const WIDGET_NAME: string = 'widget-deal-metrics';
  const ID_CONTAINER_CHART_DEAL_METRICS: string = 'container-chart-deal-metrics';
  const FIELDS_LIMIT_STATISTICS: number = 4;
  const COLUMNS_TOTAL_CAPITAL_STACK_EDIT: number = 2;
  const STACK_CHART_LABEL: string = 'Capital Stack';

  const {
    isEditMode,
    isNewDeal,
    dealMetricsStatisticsData,
    dealMetricsCapitalStackData,
    dealMetricsStatisticsNewItems,
    onAddNewItem,
    onDeleteClick,
    onEditModeButtonClick,
  } = props;

  const [dealMetricsStatisticsItems, setDealMetricsStatisticsItems]
    : [Record<string, DealMetricsStatisticsGroup> | null, Function] = useState<StateValueDefault>(null);

  const dealMetricsStatisticsItemCreatePermissions: string[] = useMemo((): string[] => [
    DEAL_METRICS_STATISTICS_ITEM_CREATE,
  ], []);
  const dealMetricsStatisticsItemDeletePermissions: string[] = useMemo((): string[] => [
    DEAL_METRICS_STATISTICS_ITEM_DELETE,
  ], []);
  const dealMetricsEditPermissions: string[] = useMemo((): string[] => [
    DEAL_METRICS_CAPITAL_STACK_ITEM_EDIT,
    DEAL_METRICS_STATISTICS_ITEM_EDIT,
  ], []);

  const isEmptyChart: boolean = useMemo((): boolean => (
    dealMetricsCapitalStackData ? dealMetricsCapitalStackData.reduce((
      isEmptyResult: boolean, dealMetricsCapitalStackItem: DealMetricsCapitalStackItem,
    ): boolean => (
      isEmptyResult && !FormatService.parseInt(dealMetricsCapitalStackItem.value)
    ), true) : true
  ), [dealMetricsCapitalStackData]);
  const chartItemList: ChartStackSingleItem[] = dealMetricsCapitalStackData && dealMetricsCapitalStackData.length
    ? dealMetricsCapitalStackData.map((
      dealMetricsCapitalStackItem: DealMetricsCapitalStackItem,
    ): ChartStackSingleItem => ({
      name: dealMetricsCapitalStackItem.name,
      value: FormatService.parseInt(dealMetricsCapitalStackItem.value),
    })) : [];
  const dealMetricsStatisticsGroups: DealMetricsStatisticsGroup[] = useMemo((): DealMetricsStatisticsGroup[] => (
    [...((dealMetricsStatisticsItems && Object.values(dealMetricsStatisticsItems)) || []),
      ...(dealMetricsStatisticsNewItems || [])]
  ), [dealMetricsStatisticsItems, dealMetricsStatisticsNewItems]);

  const isAddNewItemLimit: number = dealMetricsStatisticsData
    && dealMetricsStatisticsData.length + dealMetricsStatisticsNewItems.length >= FIELDS_LIMIT_STATISTICS;
  const formPrefixDealMetricsStatistics: string = 'dealData.dealMetricsStatisticsData';
  const formPrefixDealMetricsCapitalStack: string = 'dealData.dealMetricsCapitalStackData';
  const isEmpty: boolean = !isEditMode && !isNewDeal && (isEmptyChart
    && (!dealMetricsStatisticsData || !dealMetricsStatisticsData.length));
  const currencySign: string | null = CurrencyService.getCurrencySign(CURRENCY_USD);

  const dealMetricsStatisticsValuesDefault: DealMetricsStatisticsItem[] = [
    {
      id: 1,
      value: '',
    },
    {
      id: 2,
      value: '',
    },
    {
      id: 3,
      value: '',
    },
  ];

  const addNewItem: MouseEventHandlerFunction = useCallback((): void => {
    if (onAddNewItem && !isAddNewItemLimit) {
      onAddNewItem({
        id: StructureService.getNewItemId(dealMetricsStatisticsNewItems, dealMetricsStatisticsData),
        name: '',
        values: dealMetricsStatisticsValuesDefault,
      });
    }
  }, [isAddNewItemLimit, dealMetricsStatisticsData, dealMetricsStatisticsNewItems, dealMetricsStatisticsGroups]);

  const deleteClick: Function = useCallback((dealMetricsStatisticsItemId: number): void => {
    if (onDeleteClick) {
      onDeleteClick(dealMetricsStatisticsItemId);
    }

    if (dealMetricsStatisticsItems) {
      const newDealMetricsStatisticsItems: Record<string, DealMetricsStatisticsGroup> = dealMetricsStatisticsItems;
      if (newDealMetricsStatisticsItems[dealMetricsStatisticsItemId]) {
        delete newDealMetricsStatisticsItems[dealMetricsStatisticsItemId];
        setDealMetricsStatisticsItems({ ...newDealMetricsStatisticsItems });
      }
    }
  }, [dealMetricsStatisticsItems, dealMetricsStatisticsNewItems]);

  useEffect((): void => {
    if (dealMetricsStatisticsData) {
      const initialDealMetricsStatisticsItems: Record<string, DealMetricsStatisticsGroup> = {};

      dealMetricsStatisticsData.forEach((dealMetricsStatisticsGroup: DealMetricsStatisticsGroup): void => {
        initialDealMetricsStatisticsItems[dealMetricsStatisticsGroup.id] = { ...dealMetricsStatisticsGroup };
      });

      setDealMetricsStatisticsItems(initialDealMetricsStatisticsItems);
    }
  }, [dealMetricsStatisticsData]);

  return (
    <CheckAccess permissions={dealMetricsEditPermissions} isEnabled={isEditMode || isNewDeal}>
      <Widget
        isScrollable={isEditMode}
        isEmpty={isEmpty}
        isEditMode={isEditMode}
        withEditMode={!isNewDeal}
        editPermissions={dealMetricsEditPermissions}
        widgetName={WIDGET_NAME}
        defaultWidgetTitle={WIDGET_TITLE_DEFAULT}
        onEditModeButtonClick={onEditModeButtonClick}
      >
        {
          !isEditMode && !isNewDeal && !isEmptyChart && (
            <ChartStackSingle
              containerId={ID_CONTAINER_CHART_DEAL_METRICS}
              chartItemList={chartItemList}
              label={STACK_CHART_LABEL}
              labelFormatter={(value: number): string => (
                `${currencySign}${FormatService.formatRoundedBigNumber(value.toString())}`
              )}
            />
          )
        }
        {
          (isEditMode || isNewDeal) ? (
            <CheckAccess permissions={dealMetricsEditPermissions}>
              <Fragment>
                <Table>
                  <TableHead isSubHeader>
                    <TableRow>
                      <TableCell colSpan={COLUMNS_TOTAL_CAPITAL_STACK_EDIT}>
                        Capital Stack
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody isPlain withBorderedCells>
                    {
                      dealMetricsCapitalStackData && dealMetricsCapitalStackData.map((
                        dealMetricsCapitalStackItem: DealMetricsCapitalStackItem,
                      ): ReactElement => {
                        const formKeyDealMetricsCapitalStackItem: string = FormService.getIndexedFormFieldKey(
                          dealMetricsCapitalStackItem.id,
                        );

                        return (
                          <TableRow key={dealMetricsCapitalStackItem.id}>
                            <TableCell>
                              {dealMetricsCapitalStackItem.name}
                            </TableCell>
                            <TableCell>
                              <FormFieldNumber
                                isDisabled
                                name={`${formPrefixDealMetricsCapitalStack}`
                                  + `[${formKeyDealMetricsCapitalStackItem}].value`}
                                placeholder="Value"
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    }
                  </TableBody>
                </Table>
                <Table>
                  <TableHead isSubHeader>
                    <TableRow>
                      <TableCell colSpan={dealMetricsStatisticsGroups ? dealMetricsStatisticsGroups.length : 1}>
                        Statistics
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody isPlain withZebraRows withBorderedCells>
                    <TableRow>
                      {
                        dealMetricsStatisticsGroups.map((
                          dealMetricsStatisticsGroup: DealMetricsStatisticsGroup,
                          dealMetricsStatisticsGroupIndex: number,
                        ): ReactElement => {
                          const isLastItem: boolean = (
                            dealMetricsStatisticsGroupIndex === dealMetricsStatisticsGroups.length - 1
                          );
                          const isNewGroup: boolean = dealMetricsStatisticsNewItems
                            && !!dealMetricsStatisticsNewItems.length && !!dealMetricsStatisticsNewItems.find((
                              dealMetricsStatisticsNewGroup: DealMetricsStatisticsGroup,
                            ): boolean => (
                              dealMetricsStatisticsNewGroup.id === dealMetricsStatisticsGroup.id
                            ));
                          const formKeyDealMetricsStatisticsGroup: string = FormService.getIndexedFormFieldKey(
                            dealMetricsStatisticsGroup.id,
                          );

                          return (
                            <TableCell isActive={isNewGroup && !isNewDeal} key={dealMetricsStatisticsGroup.id}>
                              <FlexContainer>
                                <FormFieldText
                                  isBold
                                  name={`${formPrefixDealMetricsStatistics}[${formKeyDealMetricsStatisticsGroup}].name`}
                                  placeholder="Name"
                                />
                                <FlexContainer>
                                  <CheckAccess permissions={dealMetricsStatisticsItemDeletePermissions}>
                                    <Tooltip title="Delete">
                                      <ActionButton
                                        isInline
                                        withNoPadding
                                        onClick={(): void => {
                                          deleteClick(dealMetricsStatisticsGroup.id);
                                        }}
                                      >
                                        <RemoveIcon />
                                      </ActionButton>
                                    </Tooltip>
                                  </CheckAccess>
                                  {
                                    dealMetricsStatisticsData && !isAddNewItemLimit && isLastItem && (
                                      <CheckAccess permissions={dealMetricsStatisticsItemCreatePermissions}>
                                        <ActionButtonNewItem
                                          withNoLabel
                                          withNoPadding
                                          withNoHighlight
                                          onClick={addNewItem}
                                        />
                                      </CheckAccess>
                                    )
                                  }
                                </FlexContainer>
                              </FlexContainer>
                            </TableCell>
                          );
                        })
                      }
                      {
                        dealMetricsStatisticsData && dealMetricsStatisticsItems
                        && !Object.values(dealMetricsStatisticsItems).length
                        && !dealMetricsStatisticsNewItems.length && (
                          <TableCell>
                            <CheckAccess permissions={dealMetricsStatisticsItemCreatePermissions}>
                              <ActionButtonNewItem
                                withNoLabel
                                withNoPadding
                                withNoHighlight
                                onClick={addNewItem}
                              />
                            </CheckAccess>
                          </TableCell>
                        )
                      }
                    </TableRow>
                    {
                      dealMetricsStatisticsGroups && !!dealMetricsStatisticsGroups.length
                      && dealMetricsStatisticsGroups[0] && dealMetricsStatisticsGroups[0].values
                      && dealMetricsStatisticsGroups[0].values.map((
                        dealMetricsStatisticsItem: DealMetricsStatisticsItem,
                        dealMetricsStatisticsItemIndex: number,
                      ): ReactElement => (
                        <TableRow key={dealMetricsStatisticsItem.id}>
                          {
                            dealMetricsStatisticsGroups.map((
                              dealMetricsStatisticsGroup: DealMetricsStatisticsGroup,
                            ): (ReactElement | boolean)[] => {
                              const isNewGroup: boolean = dealMetricsStatisticsNewItems
                                && !!dealMetricsStatisticsNewItems.length && !!dealMetricsStatisticsNewItems.find((
                                  dealMetricsStatisticsNewGroup: DealMetricsStatisticsGroup,
                                ): boolean => (
                                  dealMetricsStatisticsNewGroup.id === dealMetricsStatisticsGroup.id
                                ));
                              const formKeyDealMetricsStatisticsGroup: string = FormService.getIndexedFormFieldKey(
                                dealMetricsStatisticsGroup.id,
                              );

                              return (
                                dealMetricsStatisticsGroup.values.map((
                                  nestedDealMetricsStatisticsItem: DealMetricsStatisticsItem,
                                  nestedDealMetricsStatisticsItemIndex: number,
                                ): ReactElement | boolean => {
                                  const formKeyNestedDealMetricsStatisticsItem: string = (
                                    FormService.getIndexedFormFieldKey(nestedDealMetricsStatisticsItem.id) as string
                                  );

                                  return (
                                    nestedDealMetricsStatisticsItemIndex === dealMetricsStatisticsItemIndex && (
                                      <TableCell
                                        isActive={isNewGroup && !isNewDeal}
                                        key={`${dealMetricsStatisticsGroup.id}-${nestedDealMetricsStatisticsItem.id}`}
                                      >
                                        <FormFieldText
                                          name={`${formPrefixDealMetricsStatistics}`
                                            + `[${formKeyDealMetricsStatisticsGroup}].values`
                                            + `[${formKeyNestedDealMetricsStatisticsItem}].value`}
                                          placeholder="Value"
                                        />
                                      </TableCell>
                                    )
                                  );
                                })
                              );
                            })
                          }
                        </TableRow>
                      ))
                    }
                  </TableBody>
                </Table>
              </Fragment>
            </CheckAccess>
          ) : (
            dealMetricsStatisticsData && !!dealMetricsStatisticsData.length && (
              <TableStackChartSingleBlock isEmptyChart={isEmptyChart}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {
                        dealMetricsStatisticsData.map((
                          dealMetricsStatisticsGroup: DealMetricsStatisticsGroup,
                        ): ReactElement => (
                          <TableCellStackChartSingle
                            isHeader
                            isTextNoWrap
                            key={dealMetricsStatisticsGroup.id}
                          >
                            {dealMetricsStatisticsGroup.name}
                          </TableCellStackChartSingle>
                        ))
                      }
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {
                      dealMetricsStatisticsGroups && !!dealMetricsStatisticsGroups.length
                      && dealMetricsStatisticsGroups[0] && dealMetricsStatisticsGroups[0].values
                      && dealMetricsStatisticsGroups[0].values.map((
                        dealMetricsStatisticsItem: DealMetricsStatisticsItem,
                        dealMetricsStatisticsItemIndex: number,
                      ): ReactElement => (
                        <TableRow key={dealMetricsStatisticsItem.id}>
                          {
                            dealMetricsStatisticsData.map((
                              dealMetricsStatisticsGroup: DealMetricsStatisticsGroup,
                            ): (ReactElement | boolean)[] => (
                              dealMetricsStatisticsGroup.values.map((
                                nestedDealMetricsStatisticsItem: DealMetricsStatisticsItem,
                                nestedDealMetricsStatisticsItemIndex: number,
                              ): ReactElement | boolean => (
                                nestedDealMetricsStatisticsItemIndex === dealMetricsStatisticsItemIndex && (
                                  <TableCellStackChartSingle
                                    key={`${dealMetricsStatisticsGroup.id}-${nestedDealMetricsStatisticsItem.id}`}
                                  >
                                    {nestedDealMetricsStatisticsItem.value}
                                  </TableCellStackChartSingle>
                                )
                              ))
                            ))
                          }
                        </TableRow>
                      ))
                    }
                  </TableBody>
                </Table>
              </TableStackChartSingleBlock>
            )
          )
        }
      </Widget>
    </CheckAccess>
  );
});

export default DealMetrics;
